"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Users,
  MessageSquare,
  BookOpen,
  Code,
  BarChart,
  Zap,
  Shield,
  Compass,
  Layers,
  Cpu,
  Globe,
  Sparkles,
} from "lucide-react";
import { cn } from "@/lib/utils";

const features = [
  {
    icon: Users,
    title: "Community Access",
    description:
      "Join our thriving community of developers and creators to share ideas, get help, and collaborate on projects.",
  },
  {
    icon: MessageSquare,
    title: "24/7 Support",
    description:
      "Get answers when you need them with our round-the-clock support team ready to help you solve any challenges.",
  },
  {
    icon: BookOpen,
    title: "Extensive Documentation",
    description:
      "Access comprehensive guides, tutorials, and API references to help you build faster and more efficiently.",
  },
  {
    icon: Code,
    title: "Developer Tools",
    description:
      "Powerful development tools designed to streamline your workflow and boost productivity across your projects.",
  },
  {
    icon: BarChart,
    title: "Analytics Dashboard",
    description:
      "Gain valuable insights with real-time analytics and customizable reports to track your application's performance.",
  },
  {
    icon: Zap,
    title: "Performance Optimization",
    description:
      "Automatically optimize your application for speed and efficiency with our intelligent performance tools.",
  },
  {
    icon: Shield,
    title: "Enterprise Security",
    description:
      "Rest easy with enterprise-grade security features including encryption, authentication, and compliance tools.",
  },
  {
    icon: Compass,
    title: "Guided Onboarding",
    description:
      "Get up and running quickly with personalized onboarding sessions and step-by-step implementation guides.",
  },
  {
    icon: Layers,
    title: "Flexible Architecture",
    description:
      "Build on a modular platform that adapts to your needs, whether you're creating a simple app or complex system.",
  },
  {
    icon: Cpu,
    title: "AI Integration",
    description:
      "Leverage cutting-edge AI capabilities to enhance your applications with intelligent features and automation.",
  },
  {
    icon: Globe,
    title: "Global CDN",
    description:
      "Deliver content lightning-fast to users worldwide with our distributed content delivery network.",
  },
  {
    icon: Sparkles,
    title: "Continuous Updates",
    description:
      "Stay ahead with regular platform updates, new features, and improvements based on community feedback.",
  },
];

function FeatureCard({
  icon: Icon,
  title,
  description,
  className,
}: {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  title: string;
  description: string;
  className?: string;
}) {
  return (
    <Card
      className={cn(
        "transition-all duration-300 border border-spacing-10 border-primary/20 bg-background/60 backdrop-blur-sm",
        "shadow-lg shadow-primary/5",
        className
      )}
    >
      <CardHeader>
        <div className="rounded-full w-12 h-12 flex items-center justify-center bg-primary/10 mb-4">
          <Icon className="h-6 w-6 text-primary" />
        </div>
        <CardTitle className="text-lg font-medium">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {description}
        </p>
      </CardContent>
    </Card>
  );
}

export default function GridList09() {
  const columnMargins = ["mt-0", "md:mt-8", "md:mt-16", "md:mt-24"];
  const numColumns = 4;
  const featuresPerColumn = features.length / numColumns;

  return (
    <section className="pb-20 pt-20 md:pb-32 md:pt-32 container mx-auto">
      <div className="text-center space-y-4 pb-16 mx-auto max-w-4xl">
        <Badge>FEATURES</Badge>
        <h2 className="mx-auto mt-4 text-3xl font-bold sm:text-5xl tracking-tight">
          Powerful Features
        </h2>
        <p className="text-xl text-muted-foreground pt-1">
          Everything you need to build modern applications at scale
        </p>
      </div>
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-[0.02] pointer-events-none" />
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-primary/10 rounded-full blur-3xl opacity-20" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-primary/10 rounded-full blur-3xl opacity-20" />

        <div className="flex flex-col md:flex-row gap-6">
          {Array.from({ length: numColumns }).map((_, columnIndex) => (
            <div
              key={columnIndex}
              className={cn(
                "flex-1 flex flex-col gap-6",
                columnMargins[columnIndex]
              )}
            >
              {features
                .slice(
                  columnIndex * featuresPerColumn,
                  (columnIndex + 1) * featuresPerColumn
                )
                .map((feature, featureIndex) => (
                  <FeatureCard
                    key={`${columnIndex}-${featureIndex}`}
                    icon={feature.icon}
                    title={feature.title}
                    description={feature.description}
                  />
                ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
